import { button, folder, levaStore, useControls } from 'leva'

import { GRASS_MAX_COUNT } from '@components/ground/Grass'
import type { CreditLinkMeta } from '@components/ui/Credits'
import { LEVELS_MAP } from '@config'
import { useGameStore } from '@stores'

export function useMainControls() {
  return useControls({
    print: button(() => {
      const data = levaStore.getData()
      console.log(
        Object.fromEntries(
          Object.entries(data)
            .filter(([, item]) => 'value' in item)
            .map(([key, item]) => [key, (item as { value: unknown }).value]),
        ),
      )
    }),
  })
}

export function useLevelControls() {
  const currentLevelIndex = useGameStore(state => state.currentLevelIndex)

  const controls = useControls(
    '🕹️ level',
    {
      level: {
        value: currentLevelIndex,
        options: Object.fromEntries(Object.entries(LEVELS_MAP).map(([key], index) => [key, index])),
      },
      paused: { value: false, label: 'pause physics' },
      ground: { value: true, label: 'show ground' },
      vehicles: { value: true, label: 'show vehicles' },
      tracks: { value: true, label: 'show tracks' },
      clouds: { value: true, label: 'show clouds' },
    },
    { order: 0, collapsed: true },
  )

  // useEffect(() => {
  //   levaStore.set(
  //     { '🕹️ level.level': Math.min(currentLevelIndex, Object.keys(LEVELS_MAP).length - 1) },
  //     false,
  //   )
  // }, [currentLevelIndex])

  return controls
}

export function useHelpersControls() {
  return useControls(
    '🛠️ helpers',
    {
      axes: false,
      gizmo: false,
      grid: {
        value: false,
        label: 'track grid',
      },
      topView: {
        value: false,
        label: 'bird view',
      },
    },
    { collapsed: true, order: 1 },
  )
}

export function useEnvironmentControls() {
  return useControls(
    '☀️ environment',
    {
      shadows: true,
      bias: { value: 0, min: -1, max: 1, step: 0.0001, label: 'shadow bias' },
      normalBias: { value: 0.15, min: -1, max: 1, step: 0.0001, label: 'shadow normal bias' },
      helpers: false,
      padding: { value: 6, step: 1, min: 0 },
      ambientLightIntensity: {
        value: 0.8,
        min: 0,
        max: 20,
        step: 0.01,
        label: 'ambient intensity',
      },
      directionalLightIntensity: {
        value: 3,
        min: 0,
        max: 20,
        step: 0.01,
        label: 'directional intensity',
      },
      sunAzimuth: {
        value: 25,
        min: 0,
        max: 360,
        step: 0.5,
        label: 'sun azimuth',
      },
      sunElevation: {
        value: 45,
        min: 1,
        max: 89,
        step: 0.5,
        label: 'sun elevation',
      },
      sunDistance: {
        value: 20,
        min: 5,
        max: 50,
        step: 0.5,
        label: 'sun distance',
      },
    },
    { collapsed: true, order: 2 },
  )
}

export function usePostProcessingControls() {
  return useControls(
    '🖼️ post processing',
    { enabled: true, bloom: true, outline: true },
    { collapsed: true, order: 3 },
  )
}

export function useGroundControls() {
  return useControls(
    '🌱 ground',
    {
      base: true,
      grass: true,
      maxCount: {
        value: GRASS_MAX_COUNT,
        min: 0,
        max: GRASS_MAX_COUNT * 2,
        step: 1,
        label: 'grass count',
      },
      shadow: { value: true, label: 'grass shadow' },
      grassLod: { value: true, label: 'grass zoom lod' },
      flowers: true,
      trees: true,
      pebbles: true,
      bushes: true,
      'bushes shadows': folder(
        {
          normalBlend: {
            value: 10,
            min: -10,
            max: 10,
            step: 0.01,
            label: 'normal blend',
          },
          bushShadow: { value: true, label: 'fake shadow' },
          bushShadowOpacity: {
            value: 0.6,
            min: 0,
            max: 1,
            step: 0.01,
            label: 'shadow opacity',
          },
          bushShadowSoftness: {
            value: 0.5,
            min: 0.1,
            max: 0.95,
            step: 0.01,
            label: 'shadow softness',
          },
          bushShadowSize: {
            value: 1.8,
            min: 0.2,
            max: 4,
            step: 0.01,
            label: 'shadow size',
          },
          bushShadowPosition: {
            value: [-0.01, 0.2, -0.07],
            step: 0.001,
            label: 'shadow position',
          },
        },
        { collapsed: true },
      ),
    },
    { collapsed: true, order: 4 },
  )
}

export function useLocomotiveControls() {
  return useControls(
    '🚂 locomotive',
    {
      smoke: true,
      radius: { value: 0.035, label: 'light radius' },
      position: { value: [-0.1, 0.342, 0.421], label: 'light position' },
    },
    { collapsed: true, order: 5 },
  )
}

export function useCreditsControls(creditLinksMeta: CreditLinkMeta[]) {
  const creditLinksSchema = Object.fromEntries(
    creditLinksMeta.map(({ id, defaultPosition, defaultRotationZ }) => [
      id,
      folder(
        {
          [`${id}_position`]: { value: defaultPosition, step: 0.1, label: 'position' },
          [`${id}_rotationZ`]: {
            value: defaultRotationZ,
            min: -180,
            max: 180,
            label: 'rotationZ',
            step: 0.1,
          },
        },
        { collapsed: true },
      ),
    ]),
  )

  return useControls(
    '🗒️ credits',
    {
      debug: false,
      groupPosition: { value: [-5, 0, 8], step: 0.1 },
      footerPosition: { value: [0.8, 0, 5], step: 0.1 },
      bmcPosition: { value: [1.1, 0, 6], step: 0.1 },
      backPosition: { value: [-2.5, 0, 0], min: -10, max: 10 },
      backRotation: { value: 0, min: -180, max: 180 },
      creditsPosition: { value: [1, 0, 0.4], min: -10, max: 10 },
      creditsRotation: { value: -30, min: -180, max: 180 },
      links: folder(creditLinksSchema, { collapsed: true }),
    },
    { collapsed: true, order: 6 },
  )
}
